import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import AIForm from "../components/ai/AIForm";
import AIResult from "../components/ai/AIResult";
import AssessmentStatus from "../components/ai/AssessmentStatus";

export default function AIPlanGenerator() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    goal_id: "acft",
    goal: "Improve ACFT Score",
    level: "Intermediate",
    weeks: 4,
    days_per_week: 4,
    equipment: ["None", "Dumbbell"],
    notes: "",
    mode: "budget",
    day_structure: ["auto", "auto", "auto", "auto"],
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [err, setErr] = useState("");
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    api.get("/me/assessment").then((r) => setAssessment(r.data || {})).catch(() => setAssessment({}));
  }, []);

  const pollJob = async (jobId) => {
    // Poll up to ~5 minutes (150 attempts × 2s).
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/plans/jobs/${jobId}`);
        if (data.progress) setProgress(data.progress);
        if (data.status === "complete") return data.plan;
        if (data.status === "failed") throw new Error(data.error || "Generation failed");
      } catch (e) {
        // Transient network blip — keep polling unless it's a 4xx
        if (e.response?.status && e.response.status < 500) throw e;
      }
    }
    throw new Error("Generation timed out. The AI coach took too long — please try again.");
  };

  const generate = async () => {
    setLoading(true); setErr(""); setPlan(null); setProgress("Starting...");
    try {
      const { data: job } = await api.post("/plans/generate", form);
      if (!job?.job_id) throw new Error("No job_id returned");
      setProgress(job.progress || "Drafting...");
      const finalPlan = await pollJob(job.job_id);
      setPlan(finalPlan);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setProgress("");
    }
  };

  const save = async () => {
    if (!plan) return;
    const payload = { name: plan.name || "AI Plan", type: plan.type || "mesocycle", goal: plan.goal, level: plan.level, weeks: plan.weeks };
    const { data } = await api.post("/plans", payload);
    navigate(`/builder/${data.id}`);
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="label-mono text-volt mb-1">// AI COACH</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black">AUTO-DRAFT MESOCYCLE</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Drop your mission parameters. The coach builds a structured FM 7-22 plan you can edit and save.</p>
      </div>
      <AssessmentStatus assessment={assessment} />
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        <AIForm form={form} setForm={setForm} loading={loading} err={err} onGenerate={generate} />
        <AIResult plan={plan} loading={loading} progress={progress} onSave={save} />
      </div>
    </Layout>
  );
}
